import { useCallback, useState } from "react";
import { toast } from "sonner";
import { SecurityPulseModal } from "../components/SecurityPulseModal";

/**
 * useSecurityPulse — wraps an axios `api` instance and returns a
 * ``request(actionType, actionRef)`` helper that resolves to a
 * short-lived pulse token. Callers pass that token to their
 * next POST as ``{ headers: { 'X-Security-Pulse': token,
 * 'X-Security-Pulse-Ref': actionRef } }``.
 *
 * Usage
 * -----
 *   const { request, modal } = useSecurityPulse(api);
 *   // In the click handler that would fire an /orders POST:
 *   const token = await request("checkout", cart.id);
 *   await api.post("/orders", body, {
 *     headers: {
 *       "X-Security-Pulse": token,
 *       "X-Security-Pulse-Ref": cart.id || "",
 *     },
 *   });
 *   // Somewhere in JSX:  { modal }
 */
export function useSecurityPulse(api) {
  const [issued, setIssued] = useState(null);   // {pulse_id, expires_at, resend_available_at, action_label, delivery_email_masked}
  const [open, setOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [pending, setPending] = useState(null); // { resolve, reject, actionType, actionRef }

  const cleanup = useCallback(() => {
    setOpen(false); setIssued(null); setPending(null); setVerifying(false);
  }, []);

  const issue = useCallback(async (actionType, actionRef) => {
    try {
      const { data } = await api.post("/security-pulse/issue",
                                       { action_type: actionType, action_ref: actionRef });
      setIssued(data);
      setOpen(true);
      return data;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not send security pulse.";
      toast.error(msg);
      throw e;
    }
  }, [api]);

  const request = useCallback((actionType, actionRef = null) => {
    return new Promise((resolve, reject) => {
      setPending({ resolve, reject, actionType, actionRef });
      issue(actionType, actionRef).catch(reject);
    });
  }, [issue]);

  const onVerify = useCallback(async (code) => {
    if (!issued || !pending) return;
    setVerifying(true);
    try {
      const { data } = await api.post("/security-pulse/verify",
                                       { pulse_id: issued.pulse_id, code });
      toast.success("Pulse verified — releasing trade.");
      const tok = data.token;
      pending.resolve(tok);
      cleanup();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wrong code");
      throw e;
    } finally { setVerifying(false); }
  }, [api, issued, pending, cleanup]);

  const onResend = useCallback(async () => {
    if (!pending) return;
    try {
      const data = await issue(pending.actionType, pending.actionRef);
      toast.success(`Fresh pulse sent to ${data.delivery_email_masked}`);
    } catch { /* toast already fired */ }
  }, [issue, pending]);

  const onClose = useCallback(() => {
    if (pending) pending.reject(new Error("Pulse cancelled"));
    cleanup();
  }, [pending, cleanup]);

  const modal = (
    <SecurityPulseModal
      open={open}
      onClose={onClose}
      pulseId={issued?.pulse_id}
      actionLabel={issued?.action_label}
      deliveryEmail={issued?.delivery_email_masked || issued?.delivery_email}
      expiresAt={issued?.expires_at}
      resendAvailableAt={issued?.resend_available_at}
      verifying={verifying}
      onVerify={onVerify}
      onResend={onResend}
    />
  );

  return { request, modal, isOpen: open };
}
